import { useCallback, useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";

import { useExportAnalysisSummary } from "@/api/analysis/useAnalysisAPI";
import axiosInstance from "@/api/config";
import {
  AIIcon,
  CloseIcon,
  ContractIcon,
  DownloadIcon,
  MaximizeIcon,
  MinimizeIcon
} from "@/assets/icons";
import { MESSAGE } from "@/constants/message";
import { useModal } from "@/context/ModalProvider";
import {
  AnalysisSummaryDataProvider,
  useAnalysisSummaryDataContext
} from "@/contexts/AnalysisSummaryDataContext";
import { useMap } from "@/hooks/map/core/useMapContext";
import {
  EModuleName,
  EModulePermission,
  useGetUserPermission
} from "@/hooks/permission/useGetUserPermission";
import { useAnalysisResultSummary } from "@/hooks/spatialAnalysis/useAnalysisResultSummary";
import { useExportAnalysisSummaryStatus } from "@/hooks/spatialAnalysis/useExportAnalysisSummaryStatus";
import useLocalStorage from "@/hooks/store/useLocalStorage";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { RootState } from "@/store";
import { setGeneralSummary } from "@/store/slice/analysisSummary";
import {
  setAnalysisResultPopup,
  setShowAnalysisSummaryPopup
} from "@/store/slice/uiSlice";
import {
  DownloadFileType,
  DownloadOnlyFileType,
  ExportDatasetFileType
} from "@/types/dataset";

import {
  LokasiAIContent,
  LokasiAIForm,
  LokasiAIInput,
  LokasiAIModal,
  LokasiAIProvider,
  ModalSize,
  useLokasiAI
} from "@lokasi-ai-web-sdk/app";
import dayjs from "dayjs";
import html2canvas from "html2canvas";
import { twMerge } from "tailwind-merge";

import { UnlockPremiumModal } from "../dataset/datasetExplorer/UnlockPremiumModal";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle
} from "../ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "../ui/dropdown-menu";
import Tooltip from "../ui/tooltip";
import { AnalysisSummaryWrapper } from "./analysisSummaryWrapper";
import PrintAnalysisSummary from "./printSummary";

interface DownloadMenuItem {
  label: string;
  fileType: DownloadFileType;
}

/**
 - Disabled until every DatasetSection has reported its summary fetch as ready
 — html2canvas would otherwise capture LoadingWrapper spinners. The span
 - wrapper is required so the Tooltip trigger still receives hover events when the DropdownMenuItem itself is disabled (which sets pointer-events: none). 
*/

const PDFDownloadMenuItem = ({ onClick }: { onClick: () => void }) => {
  const { isAllDatasetsReady } = useAnalysisSummaryDataContext();
  return (
    <Tooltip
      content={MESSAGE.DOWNLOAD_PDF_CHARTS_LOADING}
      disable={isAllDatasetsReady}
      position="left"
    >
      <span
        aria-disabled={!isAllDatasetsReady}
        className={!isAllDatasetsReady ? "block cursor-not-allowed" : "block"}
      >
        <DropdownMenuItem
          onClick={onClick}
          disabled={!isAllDatasetsReady}
          className="w-full"
        >
          PDF
        </DropdownMenuItem>
      </span>
    </Tooltip>
  );
};

const AIConversationManager = ({ analysisId }: { analysisId?: string }) => {
  const { conversationId, loadConversation } = useLokasiAI();
  const [savedConversations, setSavedConversations] = useLocalStorage<
    Record<string, string>
  >("lokasi_ai_conversations", {});

  useEffect(() => {
    if (analysisId && savedConversations[analysisId] && !conversationId) {
      loadConversation(savedConversations[analysisId], 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisId, conversationId]);

  useEffect(() => {
    if (
      analysisId &&
      conversationId &&
      savedConversations[analysisId] !== conversationId
    ) {
      setSavedConversations({
        ...savedConversations,
        [analysisId]: conversationId
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisId, conversationId]);

  return null;
};

export default function SummaryAnalysis() {
  const dispatch = useDispatch();
  const { restoreAllLayersVisibility, handleClearHighlightedGrids } = useMap();
  const { map } = useMap();
  const { showModal } = useModal();
  const {
    isAIConversationSummaryEnabled,
    isAISummaryEnabled,
    isGeoJsonDownloadEnabled,
    isFeedbackCtaEnabled,
    isDatasetWidgetEnabled
  } = useAppFeatures();

  const printRef = useRef<HTMLDivElement | null>(null);
  const dynamicChartRef = useRef<(HTMLDivElement | null)[]>([]);
  const chartRef = useRef<HTMLDivElement | null>(null);
  const promiseResolveRef = useRef<
    ((value: void | PromiseLike<void>) => void) | null
  >(null);

  const { spatialAnalysisDatasetId } = useSelector(
    (state: RootState) => state.analysisSummary
  );
  const { title: spatialTitle, latestAnalysisId } = useSelector(
    (state: RootState) => state.spatial
  );

  // Widget mode: the hook restores the saved widget config once the analysis
  // completes, then auto-fetches the summary after that restore. The restore
  // gating now lives entirely inside the hook.
  const {
    isFetching,
    isFetchingOld,
    isAutoFetchError,
    refetchAnalysisSummary
  } = useAnalysisResultSummary({
    enabled: isDatasetWidgetEnabled && !!latestAnalysisId,
    autoRestoreWidgetConfig: true
  });
  const [chartImageData, setChartImageData] = useState<string | null>(null);
  const [dynamicChartImageData, setDynamicChartImageData] = useState<string[]>(
    []
  );

  const userPermissions = useGetUserPermission();

  const aiSummaryPermission =
    userPermissions?.[EModuleName.AI_SUMMARY_ANALYSIS]?.[
      EModulePermission.VIEW
    ];

  const [isPrinting, setIsPrinting] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [mapImage, setMapImage] = useState<string | null>(null);
  const [executiveSummary, setExecutiveSummary] = useState<string>();
  const [downloadActionOpen, setDownloadActionOpen] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [wasAIModalOpen, setWasAIModalOpen] = useState(false);
  const [aiModalSize, setAiModalSize] = useState<ModalSize>("floating");
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [isAIPermissionDialogOpen, setIsAIPermissionDialogOpen] =
    useState(false);

  const { setOperationId } = useExportAnalysisSummaryStatus();
  const { mutate: exportAnalysisSummary } = useExportAnalysisSummary({
    onSuccess: (data) => {
      setOperationId(data.operation_id);
    }
  });

  const reactToPrintFn = useReactToPrint({
    documentTitle: `SummaryAnalysis${
      spatialTitle ? `_${spatialTitle}` : ""
    }_${dayjs().format("YYYYMMDDHHmm")}`,
    onBeforePrint: async () => {
      const chart = await getChartPng();
      const dynamicChartImages = await generateAllChartImages();

      if (chart) setChartImageData(chart);
      if (dynamicChartImages)
        setDynamicChartImageData(dynamicChartImages as string[]);

      return new Promise((resolve) => {
        promiseResolveRef.current = resolve;
        setIsPrinting(true);
      });
    },
    onAfterPrint: () => {
      promiseResolveRef.current = null;
      setIsPrinting(false);
    }
  });

  const getChartPng = useCallback(async () => {
    if (chartRef.current) {
      const canvas = await html2canvas(chartRef.current, {
        ignoreElements: (element) => {
          return element.classList?.contains("print:hidden") || false;
        }
      });
      return canvas.toDataURL("image/png", 1);
    }
    return null;
  }, []);

  const generateAllChartImages = useCallback(async () => {
    if (!dynamicChartRef.current) {
      return [];
    }

    const imagePromises = dynamicChartRef.current.map(async (chartElement) => {
      if (chartElement) {
        const canvas = await html2canvas(chartElement, {
          ignoreElements: (element) => {
            return element.classList?.contains("print:hidden") || false;
          }
        });
        return canvas.toDataURL("image/png", 1);
      }
      return null;
    });

    const base64Images = await Promise.all(imagePromises);
    return base64Images.filter(Boolean);
  }, []);

  const handleClose = useCallback(() => {
    dispatch(setGeneralSummary(null));
    dispatch(setShowAnalysisSummaryPopup(false));
    dispatch(setAnalysisResultPopup(true));

    handleClearHighlightedGrids(spatialAnalysisDatasetId);
    restoreAllLayersVisibility();
  }, [
    dispatch,
    handleClearHighlightedGrids,
    spatialAnalysisDatasetId,
    restoreAllLayersVisibility
  ]);

  const handleToggleAI = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!aiSummaryPermission) {
      setIsAIPermissionDialogOpen(true);
      return;
    }
    if (isExpanded) {
      setIsExpanded(false);
    }
    setShowAIModal((prev) => !prev);
  };

  const handleToggleMaximize = () => {
    const nextExpanded = !isExpanded;
    setIsExpanded(nextExpanded);
    if (nextExpanded) {
      if (showAIModal) {
        setWasAIModalOpen(true);
        setShowAIModal(false);
        setAiModalSize("floating");
      }
    } else {
      if (wasAIModalOpen) {
        setShowAIModal(true);
        setWasAIModalOpen(false);
      }
    }
  };

  const handlePrint = () => {
    if (map) {
      const canvas = map.getCanvas();
      const dataURL = canvas.toDataURL("image/png");
      setMapImage(dataURL);

      reactToPrintFn(() => printRef.current);
    }
  };

  const handleClickDownloadPDF = () => {
    setDownloadActionOpen(false);
    // If AI summary is enabled, user has permission to view AI summary, but executive summary is not ready yet, show confirmation modal before downloading PDF
    const shouldWaitForAISummary =
      isAISummaryEnabled && aiSummaryPermission && !executiveSummary;

    if (!shouldWaitForAISummary) {
      handlePrint();
    } else {
      showModal("confirm", {
        title: "Download Confirmation",
        message: MESSAGE.DOWNLOAD_PDF_AI_SUMMARY_NOT_COMPLETE,
        confirmLabel: "Download Anyway",
        cancelLabel: "Cancel",
        onCancel: () => {},
        onConfirm: () => {
          handlePrint();
        }
      });
    }
  };

  useEffect(() => {
    if (isPrinting && promiseResolveRef.current) {
      promiseResolveRef.current();
    }
  }, [isPrinting]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      handleClose();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [handleClose]);

  let modalContainerClassName = "";
  if (isExpanded) {
    modalContainerClassName =
      "h-[calc(100%-24px)] w-[calc(100%-24px)] !left-3 !right-3 !top-3";
  } else if (showAIModal) {
    modalContainerClassName = isMobile
      ? "w-[calc(100vw-24px)] h-[calc(50svh-18px)] !left-3 !right-3 !top-3"
      : "w-[calc(50vw-18px)] h-[calc(100%-24px)] !left-auto !right-3 !top-3 !bottom-3";
  } else {
    modalContainerClassName = isMobile
      ? "w-[calc(100vw-24px)] h-[calc(100%-24px)] !left-3 !right-3 !top-3"
      : "w-[514px] h-[calc(100%-24px)] !left-auto !right-24 !top-3";
  }

  const DOWNLOAD_MENU_ITEMS: DownloadMenuItem[] = [
    { label: "PDF", fileType: DownloadOnlyFileType.PDF },
    { label: "XLSX", fileType: ExportDatasetFileType.XLSX },
    { label: "CSV", fileType: ExportDatasetFileType.CSV },
    ...(isGeoJsonDownloadEnabled
      ? [{ label: "GeoJSON", fileType: ExportDatasetFileType.GEOJSON }]
      : [])
  ];

  return (
    <>
      <Dialog open onOpenChange={handleClose} modal={false}>
        {isExpanded && (
          <div
            className="fixed inset-0 bg-black/60 pointer-events-none z-[100]"
            data-testid="summary-overlay"
          />
        )}
        <DialogTitle className="sr-only">Analysis Summary</DialogTitle>
        <DialogDescription className="sr-only">
          Analysis Summary
        </DialogDescription>
        <DialogContent
          className={twMerge(
            "flex gap-0 p-0 bg-white overflow-auto transition-all duration-300",
            "!max-w-none !translate-x-0 !translate-y-0",
            modalContainerClassName,
            aiModalSize === "full" && showAIModal && "hidden"
          )}
          isHideClose
          onInteractOutside={(e) => e.preventDefault()}
        >
          <AnalysisSummaryDataProvider>
            <div className="flex w-full h-full">
              <div className="relative flex flex-col w-full h-full overflow-auto">
                <div className="summary-header flex flex-row items-center justify-between pt-4 px-4 sticky top-0 z-10 ">
                  <h1 className="text-xl font-semibold text-txtHeading">
                    Analysis Summary
                  </h1>
                  <div className="flex items-center gap-3">
                    {isAIConversationSummaryEnabled && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-auto [&_svg]:size-6"
                        aria-label="Ask AI Assistant"
                        onClick={handleToggleAI}
                      >
                        <AIIcon className="size-6 fill-dim-gray" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      className="size-auto [&_svg]:size-6"
                      size="icon"
                      aria-label={
                        isExpanded
                          ? "Minimize Analysis Summary Panel"
                          : "Maximize Analysis Summary Panel"
                      }
                      data-testid="summary-maximize-button"
                      onClick={handleToggleMaximize}
                    >
                      {isExpanded ? (
                        <MinimizeIcon className="fill-dim-gray" />
                      ) : (
                        <MaximizeIcon className="fill-dim-gray" />
                      )}
                    </Button>
                    <DropdownMenu
                      open={downloadActionOpen}
                      onOpenChange={setDownloadActionOpen}
                    >
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-auto [&_svg]:size-6"
                          aria-label="Export"
                        >
                          <DownloadIcon className="fill-dim-gray" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="center" className="z-[99999]">
                        {DOWNLOAD_MENU_ITEMS.map(({ label, fileType }) =>
                          fileType === DownloadOnlyFileType.PDF ? (
                            <PDFDownloadMenuItem
                              key={label}
                              onClick={handleClickDownloadPDF}
                            />
                          ) : (
                            <DropdownMenuItem
                              key={label}
                              onClick={() =>
                                exportAnalysisSummary({
                                  analysisId: latestAnalysisId || "",
                                  fileType
                                })
                              }
                            >
                              {label}
                            </DropdownMenuItem>
                          )
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>

                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-auto [&_svg]:size-6"
                      aria-label="Close summary panel"
                      onClick={handleClose}
                    >
                      <CloseIcon className="fill-dim-gray" />
                    </Button>
                  </div>
                </div>
                <AnalysisSummaryWrapper
                  chartRef={chartRef}
                  dynamicChartRef={dynamicChartRef}
                  isExpanded={isExpanded}
                  isFetching={
                    isDatasetWidgetEnabled ? isFetching : isFetchingOld
                  }
                  onSummaryComplete={setExecutiveSummary}
                  onCollapse={() => setIsExpanded(false)}
                  permissionAI={aiSummaryPermission}
                  isError={isDatasetWidgetEnabled && isAutoFetchError}
                  onRefetch={() => refetchAnalysisSummary()}
                />
              </div>
            </div>

            {createPortal(
              <div className="absolute left-[0] top-[0] bg-white w-full h-full hidden">
                <PrintAnalysisSummary
                  ref={printRef}
                  mapImage={mapImage}
                  chartImageData={chartImageData}
                  dynamicChartImageData={dynamicChartImageData}
                  executiveSummary={executiveSummary}
                  isExpanded={isExpanded}
                  onCollapse={() => setIsExpanded(false)}
                />
              </div>,
              document.body
            )}
          </AnalysisSummaryDataProvider>
        </DialogContent>

        {isAIConversationSummaryEnabled && (
          <LokasiAIModal
            defaultSize={aiModalSize}
            onSizeChange={(size) => setAiModalSize(size as "full" | "floating")}
            isOpen={showAIModal}
            onClose={() => {
              setShowAIModal(false);
              setAiModalSize("floating");
            }}
            position={
              isMobile
                ? {
                    top: "calc(50svh + 6px)",
                    left: 12,
                    right: 12,
                    bottom: 12
                  }
                : { top: 12, right: "calc(50vw + 6px)", bottom: 12, left: 12 }
            }
            className="z-[1000]"
          >
            <LokasiAIProvider
              httpClient={axiosInstance}
              topics={["spatial_analysis"]}
              analysisId={latestAnalysisId || undefined}
              featureFlags={{ isFeedbackCtaEnabled }}
            >
              <LokasiAIForm className="flex flex-col h-full">
                <AIConversationManager
                  analysisId={latestAnalysisId || undefined}
                />
                <div className="flex-1 overflow-auto">
                  <LokasiAIContent />
                </div>
                <LokasiAIInput placeholder="Ask anything..." />
              </LokasiAIForm>
            </LokasiAIProvider>
          </LokasiAIModal>
        )}

        {aiModalSize === "full" && showAIModal && (
          <button
            onClick={() => setAiModalSize("floating")}
            aria-label="Report & AI Summary"
            className="fixed bottom-6 left-6 z-[1001] flex items-center gap-2 px-4 py-2 bg-white border border-primary-teal rounded-2xl shadow-sm hover:bg-slate-50 transition-all active:scale-95 whitespace-nowrap"
          >
            <ContractIcon className="size-5 fill-primary-teal" />
            <span className="text-primary-teal font-semibold text-sm">
              Report & AI Summary
            </span>
          </button>
        )}
      </Dialog>
      {isAIPermissionDialogOpen &&
        !aiSummaryPermission &&
        (isAISummaryEnabled || isAIConversationSummaryEnabled) && (
          <UnlockPremiumModal
            title={MESSAGE.UNLOCK_AI_POPOVER_TITLE}
            description={MESSAGE.UNLOCK_AI_CONTENT_POPOVER_DESCRIPTION}
            contactUsDesc={
              MESSAGE.UNLOCK_AI_CONTENT_POPOVER_DESCRIPTION_CONTENT
            }
            onClose={() => setIsAIPermissionDialogOpen(false)}
            classNameContainer="z-[9999]"
          />
        )}
    </>
  );
}
